import * as React from "react";
import { useEffect, useRef, useState } from "react";
import {
  Constants,
  useDebouncedAbortableCall,
  useTranslation,
} from "hhn-commons";
import {
  AbortContext,
  CHAT_STATUS_FILTER,
  GROUP_TYPE,
  IChatListFilters,
  SERVICE_STATUS,
} from "@home-health-notify/domain";
import { FilterDataType } from "./ChatListPanel";
import FilterBox from "../common/FilterBox";
import GroupListFilter from "./GroupListFilter";
import SelectedChatListFilters from "../common/SelectedChatListFilters";
import { SearchBox } from "../common/SearchBox";
import { SearchAndFilterContainer } from "../addMember/AddMember.style";

export const defaultFilterData: FilterDataType = {
  filterBy: CHAT_STATUS_FILTER.DATE,
  patientStatus: [],
  servicesToPatient: {
    id: "",
    status: SERVICE_STATUS.PENDING,
  },
};

const prepareFilterRequest = (
  filters: IChatListFilters,
  filterData: FilterDataType,
): IChatListFilters => {
  let clonedFilterRequestData: IChatListFilters = { ...filters };

  clonedFilterRequestData["filterBy"] = filterData.filterBy;

  if (filterData?.servicesToPatient?.id) {
    let serviceToPatient = [];
    serviceToPatient.push({
      id: filterData.servicesToPatient.id,
      status: filterData.servicesToPatient.status,
    });
    clonedFilterRequestData["servicesToPatient"] = serviceToPatient;
  } else {
    // Explicitly clear
    clonedFilterRequestData["servicesToPatient"] = [];
  }

  if (filterData?.patientStatus && filterData.patientStatus.length > 0) {
    clonedFilterRequestData["patientStatus"] = filterData.patientStatus;
  } else {
    // Explicitly clear
    clonedFilterRequestData["patientStatus"] = [];
  }

  return clonedFilterRequestData;
};

const cleanFilters = (
  newFilter: IChatListFilters,
  incomingFilter: Partial<IChatListFilters>,
): IChatListFilters => {
  // Only remove patientStatus if it's explicitly set to empty in incoming filter
  if (
    "patientStatus" in incomingFilter &&
    Array.isArray(incomingFilter.patientStatus) &&
    incomingFilter.patientStatus.length === 0
  ) {
    delete newFilter.patientStatus;
  }
  // Only remove servicesToPatient if it's explicitly set to empty in incoming filter
  if (
    "servicesToPatient" in incomingFilter &&
    Array.isArray(incomingFilter.servicesToPatient) &&
    incomingFilter.servicesToPatient.length === 0
  ) {
    delete newFilter.servicesToPatient;
  }
  // Remove filterBy if set to default
  if (newFilter.filterBy === CHAT_STATUS_FILTER.DATE) {
    delete newFilter.filterBy;
  }

  return newFilter;
};

export const ChatListSearchAndFilter = ({
  showSearchPanel,
  showChatListPanel,
  groupType,
  fetchChats,
  resetChats,
}: {
  showSearchPanel: () => void;
  showChatListPanel: () => void;
  groupType: GROUP_TYPE;
  fetchChats: any;
  resetChats: any;
}) => {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState("");
  const [filters, setFilters] = useState<IChatListFilters>({ search: "" });
  const [filterData, setFilterData] =
    useState<FilterDataType>(defaultFilterData);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filterDraft, setFilterDraft] =
    useState<FilterDataType>(defaultFilterData);

  const filtersRef = useRef<IChatListFilters>(filters);
  const hasMounted = useRef(false);

  // Keep ref in sync with state
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    const isSearchOrFilterApplied = !(
      Object.keys(filters).length === 1 && filters.search === ""
    );
    if (isSearchOrFilterApplied) {
      showSearchPanel();
    } else {
      resetChats();
      showChatListPanel();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const handleFilterOpen = () => {
    // clone so edits don’t mutate the applied state
    setFilterDraft({
      ...filterData,
      servicesToPatient: { ...filterData.servicesToPatient },
      patientStatus: [...(filterData.patientStatus ?? [])],
    } as FilterDataType);
    setIsFilterOpen(true);
  };

  const handleFilterClose = () => {
    setIsFilterOpen(false);
  };

  const applyFilters = async (
    filters: IChatListFilters,
    context?: AbortContext,
  ): Promise<void> => {
    setFilters(filters);
    const isEmptyFilter =
      Object.keys(filters).length === 1 && filters.search === "";
    if (!isEmptyFilter) {
      await fetchChats(filters, true, context);
    }
  };

  const updateFilterAndApply = async (
    filter: Partial<IChatListFilters>,
    context?: AbortContext,
  ) => {
    const newFilter = {
      ...filtersRef.current,
      ...filter,
    };
    const cleanedFilters = cleanFilters(newFilter, filter);
    await applyFilters(cleanedFilters, context);
  };

  const debouncedSearch = useDebouncedAbortableCall(
    async (abortContext: AbortContext, search: string) => {
      await updateFilterAndApply({ search }, abortContext);
    },
    Constants.API_DEBOUNCE_DELAY_IN_MS,
  );

  useEffect(() => {
    // Skip the effect on the first render
    if (!hasMounted.current) {
      hasMounted.current = true;
      return;
    }
    debouncedSearch(searchText);
  }, [searchText, debouncedSearch]);

  const onSearchTextChange = (searchText: string) => {
    setSearchText(searchText);
  };

  const onSearchClear = () => {
    setSearchText("");
  };

  const onSetFilterData = (key: string, value: any) => {
    if (isFilterOpen) {
      setFilterDraft(prev => ({ ...prev, [key]: value }));
    } else {
      setFilterData(prev => ({ ...prev, [key]: value }));
    }
  };

  const onFilter = async () => {
    // apply the draft filters
    setFilterData(filterDraft);
    const filterRequest = prepareFilterRequest(filtersRef.current, filterDraft);
    await updateFilterAndApply(filterRequest);
  };

  const onReset = async () => {
    setFilterData(defaultFilterData);
    await applyFilters({ search: filtersRef.current.search || "" });
  };

  const getFilterCount = () => {
    let count = 0;
    if (filters.filterBy && filters.filterBy.length > 0) {
      count++;
    }
    if (filters.servicesToPatient) {
      count += filters.servicesToPatient.length;
    }
    if (filters.patientStatus) {
      count += filters.patientStatus.length;
    }
    return count;
  };

  return (
    <SearchAndFilterContainer>
      <SearchBox
        searchText={searchText}
        onSearchClear={onSearchClear}
        onSearchTextChange={onSearchTextChange}
      >
        <FilterBox
          title={t("filter.messageFilter")}
          onApply={onFilter}
          onReset={onReset}
          filterCount={getFilterCount()}
          onOpen={handleFilterOpen}
          onClose={handleFilterClose}
        >
          <GroupListFilter
            filterData={isFilterOpen ? filterDraft : filterData}
            onSetFilterData={onSetFilterData}
            groupType={groupType}
          />
        </FilterBox>
      </SearchBox>
      <SelectedChatListFilters
        filters={filters}
        filterCount={getFilterCount()}
      />
    </SearchAndFilterContainer>
  );
};
